import React, { FunctionComponent, useEffect, useState } from 'react'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useApi } from 'components/providers/ApiProvider'
import { useMessage } from 'components/elements/Message'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import { useSubscriptionOffers } from 'SubscriptionOffers/SubscriptionOffersProvider'
import { TextField } from 'components/elements/TextField'
import { DateField } from 'components/elements/DateField'
import { formatDateFieldToDate, formatToDateField, parseDateField, yupDateField } from 'utils'
import { Shape } from 'yup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import { Checkbox } from '@material-ui/core'

export interface EditSubscriptionOfferDialogData {
  id: number
  price: number
  validFrom: string
  validTo: string | null
  maxActiveSubscriptions: number | null
  autoRenew: boolean
}

type Props = {
  data: EditSubscriptionOfferDialogData
}
type Functions = {}
type AllProps = Props & Functions

const EditSubscriptionOfferDialog: FunctionComponent<AllProps> = ({ data }) => {
  if (!data) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()
  const { setLoadList } = useSubscriptionOffers()
  const { showSuccess, showError } = useMessage()

  const [open, setOpen] = useState(false)

  useEffect(() => {
    setOpen(true)
  }, [data])

  const handleEnter = () => {
    if (data.price) {
      formal.change('price', (data.price / 100).toFixed(2))
    }
    formal.change('validFrom', formatToDateField(data.validFrom))
    if (data.validTo) {
      formal.change('validTo', formatToDateField(data.validTo))
    }
    if (data.maxActiveSubscriptions) {
      formal.change('maxActiveSubscriptions', data.maxActiveSubscriptions)
    }
    if (data.autoRenew) {
      formal.change('autoRenew', data.autoRenew)
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    formal.change('price', initialValues.price)
    formal.change('validFrom', initialValues.validFrom)
    formal.change('validTo', initialValues.validTo)
    formal.change('maxActiveSubscriptions', initialValues.maxActiveSubscriptions)
    formal.change('autoRenew', initialValues.autoRenew)
    formal.clearErrors()
  }

  const initialValues: Shape<object, {
    price: number | string
    validFrom: string
    validTo: string
    maxActiveSubscriptions: number | ''
    autoRenew: boolean
  }> = {
    price: '0.00',
    validFrom: '',
    validTo: '',
    maxActiveSubscriptions: '',
    autoRenew: false,
  }

  const schema = yup.object().shape({
    price: yup.number().required().min(0).test({
      test: value => {
        return (Math.round(value * 100) / 100) == value
      },
    }),
    validFrom: yupDateField({ required: true }),
    validTo: yupDateField(),
    maxActiveSubscriptions: yup.lazy(value =>
      value === ''
        ? yup.string()
        : yup.number().required().integer().min(1),
    ),
    autoRenew: yup.boolean(),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.updateSubscriptionOffer({
        offer_id: data.id,
        price: Math.round(Number(values.price) * 100),
        valid_from: formatDateFieldToDate(values.validFrom),
        valid_to: values.validTo ? formatDateFieldToDate(values.validTo) : null,
        max_active_subscriptions: values.maxActiveSubscriptions ? values.maxActiveSubscriptions : null,
        auto_renew: Number(values.price) ? values.autoRenew : undefined,
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          handleClose()

          setLoadList()

          showSuccess(t('messages.subscriptionOfferUpdated'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotUpdateSubscriptionOffer')} ${message}`)
        })
    },
  })

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose} TransitionProps={{ onEnter: handleEnter, onExited: handleExited }}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('editSubscriptionOffer')}</DraggableDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <TextField
              GridProps={{ xs: 12 }}
              label={t('price')}
              required
              {...formal.getFieldProps('price')}
            />
            <DateField
              GridProps={{ xs: 6 }}
              label={t('startDate')}
              required
              {...(formal.values.validTo && { maxDate: parseDateField(formal.values.validTo) })}
              {...formal.getFieldProps('validFrom')}
            />
            <DateField
              GridProps={{ xs: 6 }}
              label={t('endDate')}
              clearable
              {...(formal.values.validFrom && { minDate: parseDateField(formal.values.validFrom) })}
              {...formal.getFieldProps('validTo')}
            />
            <TextField
              GridProps={{ xs: 12 }}
              type="number"
              label={t('numberOfSubscriptions')}
              inputProps={{
                min: 1,
              }}
              helperText={t('maxActiveSubscriptions')}
              {...formal.getFieldProps('maxActiveSubscriptions')}
            />
            <Grid item xs={12} style={{ paddingLeft: 19 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    {...formal.getFieldProps('autoRenew')}
                    checked={formal.values.autoRenew}
                    onChange={(event) => formal.change('autoRenew', event.target.checked)}
                    disabled={!Number(formal.values.price)}
                  />
                }
                label={t('withAutoRenew')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formal.isSubmitting}
          >
            {t('edit')}
          </Button>
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default EditSubscriptionOfferDialog
